import { useState, useEffect } from 'react';
import apiService from '../../services/api.service';
import './AS2Connectors.css';

const AS2Connectors = () => {
    const [connectors, setConnectors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingConnector, setEditingConnector] = useState(null);
    const [saving, setSaving] = useState(false);

    const fetchConnectors = async () => {
        try {
            setLoading(true);
            const data = await apiService.get('/as2/connectors');
            if (Array.isArray(data)) {
                setConnectors(data);
            }
        } catch (err) {
            console.error('Error fetching connectors:', err);
            setError('Failed to load AS2 connectors');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchConnectors();
    }, []);

    const handleEdit = (connector) => {
        setEditingConnector({
            ...connector,
            name: connector.name || '',
            email: connector.email || '',
            address: connector.address || ''
        });
    };

    const handleSave = async () => {
        if (!editingConnector) return;

        setSaving(true);
        try {
            const response = await apiService.post('/as2/connectors/details', {
                connectorId: editingConnector.connectorId,
                name: editingConnector.name,
                email: editingConnector.email,
                address: editingConnector.address
            });

            if (response.success) {
                fetchConnectors();
                setEditingConnector(null);
            } else {
                alert('Failed to save connector details');
            }
        } catch (err) {
            console.error('Error saving connector details:', err);
            alert('An error occurred while saving');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="connectors-loading">Loading AS2 Connectors...</div>;
    if (error) return <div className="connectors-error">{error}</div>;

    return (
        <>
            <header className="top-header">
                <div className="breadcrumb">
                    <span>Partner Connections</span>
                    <span className="breadcrumb-separator">/</span>
                    <span className="breadcrumb-current">AS2 Connectors</span>
                </div>
            </header>
            <div className="connectors-page">
                <div className="connectors-header">
                    <h1>AS2 Connectors</h1>
                    <p className="subtitle">Manage AWS Transfer Family AS2 connectors and their metadata</p>
                </div>

                <div className="connectors-grid">
                    {connectors.length === 0 ? (
                        <div className="no-connectors">
                            <p>No AS2 connectors found. Configure them in AWS Transfer Family.</p>
                        </div>
                    ) : (
                        connectors.map(connector => (
                            <div key={connector.connectorId} className="connector-card">
                                <div className="connector-header">
                                    <div className="connector-icon">
                                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="28" height="28">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                                d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"></path>
                                        </svg>
                                    </div>
                                    <div className="connector-info">
                                        <h3>{connector.name || 'Unnamed Connector'}</h3>
                                        <span className="connector-id">{connector.connectorId}</span>
                                    </div>
                                    <span className="connector-status">Active</span>
                                </div>

                                <div className="connector-details">
                                    <div className="detail-item">
                                        <span className="label">URL</span>
                                        <span className="value url">{connector.url || 'N/A'}</span>
                                    </div>
                                    {connector.email && (
                                        <div className="detail-item">
                                            <span className="label">Email</span>
                                            <span className="value">{connector.email}</span>
                                        </div>
                                    )}
                                    {connector.address && (
                                        <div className="detail-item">
                                            <span className="label">Address</span>
                                            <span className="value">{connector.address}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="connector-actions">
                                    <button
                                        className="action-btn primary"
                                        onClick={() => handleEdit(connector)}
                                    >
                                        Edit Metadata
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {editingConnector && (
                    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setEditingConnector(null)}>
                        <div className="connector-modal">
                            <div className="modal-header">
                                <h2>Edit Connector Metadata</h2>
                                <button className="modal-close" onClick={() => setEditingConnector(null)}>
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                                    </svg>
                                </button>
                            </div>

                            <div className="modal-body">
                                {/* Connector Info Section */}
                                <div className="form-section">
                                    <div className="section-header">
                                        <span className="section-icon">🔌</span>
                                        <span>Connector Information</span>
                                    </div>
                                    <div className="form-row single">
                                        <div className="form-field">
                                            <label>AWS Connector ID</label>
                                            <input
                                                type="text"
                                                value={editingConnector.connectorId}
                                                disabled
                                                className="disabled-input"
                                            />
                                            <span className="field-hint">This ID is assigned by AWS Transfer Family</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Display Settings Section */}
                                <div className="form-section">
                                    <div className="section-header">
                                        <span className="section-icon">🏷️</span>
                                        <span>Display Settings</span>
                                    </div>
                                    <div className="form-row single">
                                        <div className="form-field">
                                            <label>Display Name <span className="required">*</span></label>
                                            <input
                                                type="text"
                                                value={editingConnector.name}
                                                onChange={(e) => setEditingConnector({ ...editingConnector, name: e.target.value })}
                                                placeholder="Acme Corp AS2 Connection"
                                            />
                                            <span className="field-hint">A friendly name to identify this connector</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Contact Section */}
                                <div className="form-section">
                                    <div className="section-header">
                                        <span className="section-icon">📧</span>
                                        <span>Contact Information</span>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-field">
                                            <label>Contact Email</label>
                                            <input
                                                type="email"
                                                value={editingConnector.email}
                                                onChange={(e) => setEditingConnector({ ...editingConnector, email: e.target.value })}
                                                placeholder="edi@partner.com"
                                            />
                                        </div>
                                        <div className="form-field">
                                            <label>Address / Notes</label>
                                            <input
                                                type="text"
                                                value={editingConnector.address}
                                                onChange={(e) => setEditingConnector({ ...editingConnector, address: e.target.value })}
                                                placeholder="123 Main St or additional notes"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    className="btn-cancel"
                                    onClick={() => setEditingConnector(null)}
                                    disabled={saving}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn-save"
                                    onClick={handleSave}
                                    disabled={saving || !editingConnector.name}
                                >
                                    {saving ? 'Saving...' : 'Save Metadata'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default AS2Connectors;
